import { Icon } from '@/components/Icon'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { attachments as api } from '@/api/endpoints'
import { queryKeys } from '@/api/queryKeys'
import type { AttachmentResponse } from '@/api/types'
import { cx } from '@/lib/cx'
import { AttachmentViewer } from './AttachmentViewer'

function Thumb({ attachment }: { attachment: AttachmentResponse }) {
  const { data } = useQuery({
    queryKey: queryKeys.thumbnail(attachment.id),
    queryFn: () => api.thumbnail(attachment.id),
    enabled: attachment.has_thumbnail && attachment.status === 'ready',
    // Signed URLs expire in five minutes; refetching sooner than that keeps the
    // history screen working on a page left open between patients.
    staleTime: 4 * 60_000,
  })

  if (data?.thumbnail_url) {
    return (
      <img
        className="size-full object-cover"
        src={data.thumbnail_url}
        // Filename and kind, never an attempt to describe the clinical content
        // of the image.
        alt={`${attachment.kind} — ${attachment.original_filename}`}
      />
    )
  }
  return (
    <span className="text-2xl text-fg-muted">
      <Icon name={attachment.content_type === 'application/pdf' ? 'file' : 'image'} size="2rem" />
    </span>
  )
}

export function AttachmentStrip({ attachments }: { attachments: AttachmentResponse[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const viewable = attachments.filter((a) => a.status === 'ready')

  return (
    <>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {attachments.map((attachment, index) => (
          <div className="flex w-28 flex-none flex-col gap-1" key={attachment.id}>
            <button
              type="button"
              className="relative grid size-28 place-items-center overflow-hidden rounded-md border border-line-soft bg-subtle p-0"
              onClick={() => setOpenIndex(viewable.findIndex((a) => a.id === attachment.id))}
              disabled={attachment.status !== 'ready'}
              aria-label={`Open ${attachment.original_filename}`}
            >
              <Thumb attachment={attachment} />
            </button>
            <span className="truncate text-xs text-fg-muted" title={attachment.original_filename}>
              {attachment.original_filename}
            </span>
            {/* A failed attachment stays visible and says so. Letting it vanish
                would leave the doctor believing the X-ray is in the record. */}
            {attachment.status !== 'ready' && (
              <span
                className={cx('text-xs', attachment.status === 'failed' && 'text-danger')}
              >
                {attachment.status === 'failed'
                  ? 'Upload failed'
                  : attachment.status === 'quarantined'
                    ? 'Rejected'
                    : 'Processing…'}
              </span>
            )}
            {index === -1 && null}
          </div>
        ))}
      </div>
      {openIndex !== null && openIndex >= 0 && viewable[openIndex] && (
        <AttachmentViewer
          attachments={viewable}
          index={openIndex}
          onIndexChange={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  )
}
